import { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MoreVertIcon from '@mui/icons-material/MoreVert';

/**
 * ActionMenu — a 3-dot icon button that opens a dropdown list of actions
 .*
 * Props:
 *   actions:          Array<{ label: string, onClick: () => void, icon?: ReactNode }>
 *   hideUntilRowHover boolean — when true, the icon is invisible until the parent <tr> is hovered
 *                              (always visible on touch devices)
 */
export default function ActionMenu({ actions, hideUntilRowHover = false }) {
  const [anchor, setAnchor] = useState(null);

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchor(e.currentTarget);
  };

  const handleClose = (e) => {
    if (e) e.stopPropagation();
    setAnchor(null);
  };

  const handleAction = (e, onClick) => {
    e.stopPropagation();
    setAnchor(null);
    onClick();
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        sx={{ color: 'var(--grey-400)',
          '&:hover': { color: 'var(--grey-700)' },
          ...(hideUntilRowHover && !anchor ? {
            visibility: 'hidden',
            'tr:hover &': { visibility: 'visible' },
            '@media (pointer: coarse)': { visibility: 'visible' },
          } : {}),
        }}
      >
        <MoreVertIcon sx={{ fontSize: 18 }} />
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            border: '1px solid var(--grey-200)',
            borderRadius: '6px',
            minWidth: 180,
          },
        }}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.label}
            onClick={(e) => handleAction(e, action.onClick)}
            sx={{ py: '8px', px: 2 }}
          >
            {action.icon && (
              <ListItemIcon sx={{ minWidth: 28, color: 'var(--grey-600)' }}>
                {action.icon}
              </ListItemIcon>
            )}
            <ListItemText
              primary={action.label}
              primaryTypographyProps={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
